/**
 * Unified issue summary for the "issues are present and obvious" surface.
 *
 * Collapses every issue source that already flows through buildGateItem()
 * (contract violations, backend-status, human-QA, human-verify, slug-collision)
 * PLUS the two sources being folded in for this feature (provenance = template
 * fallback, and missing image alt) into ONE count the Publish-adjacent badge and
 * the gate dialog both read — so they can never disagree.
 *
 * ⚠ INTERFACE IS LOCKED (contract for the parallel GLM sessions). Session A fills
 * in the body; sessions B and C import `IssueCategory`, `IssueBadgeSummary`, and
 * `summarizeGateIssues` against exactly these shapes — do NOT change signatures.
 */
import type { GateItem } from './prePublishGate';
// IssueCategory is defined in metadataCriteria.ts (the lowest-level module) to
// avoid a circular type import. Re-exported here so consumers import from the
// "issue summary" surface as the locked contract requires.
export type { IssueCategory } from './metadataCriteria';
import type { IssueCategory } from './metadataCriteria';

/** Issue TYPE buckets (Carl's "group by type"). Set on MetadataViolation.category. */
// Re-exported above; the local import keeps the rest of this file type-safe.

export interface IssueBadgeSummary {
  /** All distinct issues to look at, deduped per field. The badge's main number. */
  total: number;
  /** Subset with tier === 'block' — what actually stops publish without sign-off. */
  blocking: number;
  /** total - blocking (warn/info/provenance) — the "review" tail. */
  review: number;
  /** Count per IssueCategory, for the grouped dialog + tooltip. */
  byCategory: Partial<Record<IssueCategory, number>>;
}

const EMPTY: IssueBadgeSummary = { total: 0, blocking: 0, review: 0, byCategory: {} };

/** Tier priority: block (3) > warn (2) > info (1). Higher wins. */
function tierRank(tier: string): number {
  if (tier === 'block') return 3;
  if (tier === 'warn') return 2;
  return 1;
}

/**
 * Summarize a GateItem's violations into the unified badge payload.
 *
 * Dedup is PER FIELD: a field with both a contract violation and a provenance
 * violation counts as ONE total (the field needs attention once). Pseudo-fields
 * (status, human_qa, human_verify, images) each count once regardless of how
 * many violations they carry.
 *
 * For each distinct field:
 *  - blocking = true when ANY violation on that field has tier 'block'.
 *  - category = the category of the highest-tier violation on that field (first
 *    in array order at the winning tier). This means a field with a block-tier
 *    contract violation + a warn-tier provenance violation counts as blocking
 *    and under 'contract' — the most actionable issue wins.
 *
 * Returns EMPTY (total 0) for a gate item with no violations so the badge
 * renders "clear".
 */
export function summarizeGateIssues(item: GateItem): IssueBadgeSummary {
  const violations = item.violations;
  if (!violations || violations.length === 0) return EMPTY;

  // Group violations by field, tracking the winning (highest-tier, first-seen)
  // category and whether the field has any block-tier violation.
  const fieldMap = new Map<
    string,
    { hasBlock: boolean; bestRank: number; category: IssueCategory }
  >();

  for (const v of violations) {
    const field = v.field;
    const rank = tierRank(v.tier);
    const cat: IssueCategory = v.category ?? 'contract';
    const existing = fieldMap.get(field);
    if (!existing) {
      fieldMap.set(field, {
        hasBlock: v.tier === 'block',
        bestRank: rank,
        category: cat,
      });
    } else {
      // Merge: a block on ANY violation makes the field blocking.
      if (v.tier === 'block') existing.hasBlock = true;
      // A higher-tier violation's category wins. Ties keep the first-seen
      // category (array order: contract violations are appended first by
      // buildGateItem, so contract wins over provenance at the same tier).
      if (rank > existing.bestRank) {
        existing.bestRank = rank;
        existing.category = cat;
      }
    }
  }

  let total = 0;
  let blocking = 0;
  const byCategory: Partial<Record<IssueCategory, number>> = {};

  for (const { hasBlock, category } of fieldMap.values()) {
    total += 1;
    if (hasBlock) {
      blocking += 1;
    }
    byCategory[category] = (byCategory[category] ?? 0) + 1;
  }

  return {
    total,
    blocking,
    review: total - blocking,
    byCategory,
  };
}
